import { Alert, AlertType, Icon } from '@toggle/design-system';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  TOKEN_ALLOWANCE_FEATURE_FLAG,
  useFlags,
} from '~/stores/use-flags/useFlags';
import { ToastPosition, useToasts } from '~/stores/use-toasts/useToasts';
import { formatTokenUsageResetDate } from '~/stores/use-token-usage/token-usage-utils';
import {
  type TokenUsageStatus,
  type TokenUsageUpdateSource,
  useTokenUsage,
} from '~/stores/use-token-usage/useTokenUsage';

import styles from './useTokenUsageLimitToast.module.scss';

export const TOKEN_USAGE_LIMIT_TOAST_ID = 'token-usage-limit-exceeded';

const shouldShowLimitExceededToast = (
  source: TokenUsageUpdateSource | undefined
): boolean =>
  source === 'reconnect' || source === 'websocket' || source === 'conversation';

export const useTokenUsageLimitToast = () => {
  const { t } = useTranslation('chat');
  const isFeatureEnabled = useFlags(state =>
    state.getFeatureFlagState(TOKEN_ALLOWANCE_FEATURE_FLAG)
  );
  const { removeToast, showToast } = useToasts(state => ({
    removeToast: state.removeToast,
    showToast: state.showToast,
  }));
  const { resetAt, source, status } = useTokenUsage(state => ({
    resetAt: state.usage?.reset_at,
    source: state.lastUpdateSource,
    status: state.status,
  }));
  const previousStatusRef = useRef<TokenUsageStatus>(status);

  useEffect(() => {
    const previousStatus = previousStatusRef.current;
    previousStatusRef.current = status;

    if (
      !isFeatureEnabled ||
      previousStatus === 'exhausted' ||
      status !== 'exhausted' ||
      !shouldShowLimitExceededToast(source)
    ) {
      return;
    }

    const formattedResetDate = formatTokenUsageResetDate(resetAt);
    const title = formattedResetDate
      ? t('chat:tokenUsage.limitReachedToastTitle', {
          resetDate: formattedResetDate,
        })
      : t('chat:tokenUsage.limitReachedToastTitleWithoutReset');
    const message = formattedResetDate
      ? t('chat:tokenUsage.limitReachedToastMessage', {
          resetDate: formattedResetDate,
        })
      : t('chat:tokenUsage.limitReachedToastMessageWithoutReset');

    showToast({
      id: TOKEN_USAGE_LIMIT_TOAST_ID,
      position: ToastPosition.TopRight,
      timeout: 6000,
      content: (
        <Alert
          className={styles.toast}
          customIcon={
            <Icon
              fillColor="var(--icon-warning)"
              iconName="LockKey"
              size={20}
              viewBox="0 0 20 20"
            />
          }
          iconClassName={styles.icon}
          data-testid="token-usage-limit-toast"
          message={message}
          onClose={() => removeToast(TOKEN_USAGE_LIMIT_TOAST_ID)}
          title={title}
          type={AlertType.Info}
        />
      ),
    });
  }, [isFeatureEnabled, removeToast, resetAt, showToast, source, status, t]);
};
